<template>
  <div class="device-search" @keydown.escape="close">
    <input
      ref="inputRef"
      v-model="q"
      type="text"
      class="form-control device-search-input"
      :placeholder="placeholder"
      autocomplete="off"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.down.prevent="moveCursor(1)"
      @keydown.up.prevent="moveCursor(-1)"
      @keydown.enter.prevent="commitCursor"
    />

    <div v-if="open" class="device-search-dropdown">
      <div v-if="loading" class="device-search-state">Suche…</div>
      <div v-else-if="error" class="device-search-state device-search-error">{{ error }}</div>
      <div v-else-if="visibleResults.length === 0" class="device-search-state">Keine Treffer.</div>
      <ul v-else>
        <li
          v-for="(d, idx) in visibleResults"
          :key="d.id"
          :class="{ active: idx === cursor }"
          @mousedown.prevent="select(d)"
          @mouseenter="cursor = idx"
        >
          <span class="device-search-id">#{{ d.id }}</span>
          <span class="device-search-desc">{{ d.description || '—' }}</span>
          <span v-if="d.ek_price != null" class="device-search-price">{{ formatPrice(d.ek_price) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { searchDevices, type DeviceSearchResult, ApiError } from '../services/repairRecipes';

const props = defineProps<{
  placeholder?: string;
  excludeIds?:  number[];
  typeId?:      number;
}>();

const emit = defineEmits<{
  (e: 'update:selected', value: DeviceSearchResult): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const q        = ref('');
const results  = ref<DeviceSearchResult[]>([]);
const loading  = ref(false);
const error    = ref<string | null>(null);
const open     = ref(false);
const cursor   = ref(0);

const visibleResults = computed(() => {
  const exclude = new Set(props.excludeIds ?? []);
  return results.value.filter(d => !exclude.has(d.id));
});

let debounceHandle: number | null = null;

watch(q, (value) => {
  if (debounceHandle !== null) {
    window.clearTimeout(debounceHandle);
  }
  const term = value.trim();
  if (term.length < 2) {
    results.value = [];
    error.value   = null;
    loading.value = false;
    open.value    = term.length > 0;
    return;
  }
  loading.value = true;
  error.value   = null;
  open.value    = true;
  debounceHandle = window.setTimeout(() => {
    runSearch(term);
  }, 250);
});

async function runSearch(term: string) {
  try {
    const list  = await searchDevices(term, 20, props.typeId);
    results.value = list;
    cursor.value  = 0;
  } catch (e) {
    if (e instanceof ApiError) {
      error.value = e.code === 'BAD_REQUEST' ? 'Suchbegriff zu kurz.' : `Fehler: ${e.message}`;
    } else {
      error.value = 'Unbekannter Fehler.';
    }
    results.value = [];
  } finally {
    loading.value = false;
  }
}

function handleFocus() {
  if (q.value.trim().length > 0) {
    open.value = true;
  }
}

function handleBlur() {
  // kleiner Delay, damit mousedown noch durchkommt
  window.setTimeout(() => { open.value = false; }, 100);
}

function moveCursor(delta: number) {
  if (!open.value || visibleResults.value.length === 0) return;
  const next = cursor.value + delta;
  if (next < 0) cursor.value = visibleResults.value.length - 1;
  else if (next >= visibleResults.value.length) cursor.value = 0;
  else cursor.value = next;
}

function commitCursor() {
  const d = visibleResults.value[cursor.value];
  if (d) select(d);
}

function select(d: DeviceSearchResult) {
  emit('update:selected', d);
  q.value      = '';
  results.value = [];
  open.value   = false;
  inputRef.value?.blur();
}

function close() {
  open.value = false;
}

function formatPrice(value: number): string {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(value);
}

onBeforeUnmount(() => {
  if (debounceHandle !== null) window.clearTimeout(debounceHandle);
});
</script>

<style scoped>
.device-search {
  position: relative;
  width: 100%;
}

.device-search-input {
  width: 100%;
}

.device-search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  z-index: 30;
  max-height: 280px;
  overflow-y: auto;
}

.device-search-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.device-search-dropdown li {
  display: grid;
  grid-template-columns: 70px 1fr auto;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  cursor: pointer;
  font-size: 0.92rem;
}

.device-search-dropdown li.active,
.device-search-dropdown li:hover {
  background: #fff5e0;
}

.device-search-id {
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

.device-search-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-search-price {
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

.device-search-state {
  padding: 0.55rem 0.7rem;
  color: #6c757d;
  font-size: 0.92rem;
}

.device-search-error {
  color: #b91c1c;
}
</style>
